// Elevator: the floor selector (vertical on desktop, horizontal chips on mobile) with the building
// view button and per-floor activity; plus the scene zoom controls. The vertical list reads like
// the building: the highest floor at the top, 01 at the bottom; the phone chips keep 01 on the
// left. Office actions (new floor, rename, new department) live in the top bar's menu only.
import { h, icon, setText, setAttr, classNames, clear, iconButton } from '../dom.js';
import { t } from '../i18n/fr.js';
import { sortedFloors, floorDepartments } from '../store.js';
import { deptColor, pad2 } from '../format.js';
import { floorNumber } from '../scene/layout.js';
import { badgeText } from './notifications.js';

const MOBILE_QUERY = '(max-width: 767px)';

/**
 * The floors in the order the elevator lists them (pure): natural order (01 first) for the
 * horizontal chips, top-down (highest number first) for the vertical list. Each entry keeps its
 * real index (`idx`) so the floor number never depends on the display order.
 * @param {object[]} floors
 * @param {{vertical?: boolean}} [opts]
 * @returns {Array<{floor: object, idx: number}>}
 */
export function elevatorOrder(floors, { vertical = true } = {}) {
  const list = sortedFloors(floors).map((floor, idx) => ({ floor, idx }));
  return vertical ? list.reverse() : list;
}

/**
 * Activity of a floor for the elevator dot: 'waiting' when drafts wait, 'working' when tasks run,
 * else 'idle' (pure).
 */
export function floorActivity(stats) {
  if (!stats) return 'idle';
  if (stats.waiting > 0) return 'waiting';
  if (stats.doing > 0) return 'working';
  return 'idle';
}

/**
 * Creates the elevator.
 * @param {object} ctx app context
 * @param {{onSelectFloor: (floorId: string) => void, onBuilding: () => void}} handlers
 * @returns {{el: HTMLElement, destroy(): void}}
 */
export function createElevator(ctx, { onSelectFloor, onBuilding }) {
  const { store } = ctx;
  const list = h('ol', { class: 'elevator-list' });
  const buildingBtn = h('button', { type: 'button', class: 'elevator-building', onClick: () => onBuilding?.(), title: `${t('elevator.building')} (B)` },
    icon('layers', { size: 16 }), h('span', { class: 'mono-label' }, t('elevator.building')));
  const el = h('nav', { class: 'elevator', 'aria-label': t('elevator.label') },
    h('p', { class: 'elevator-title mono-label', 'aria-hidden': 'true' }, t('elevator.label')), list, buildingBtn);
  const items = new Map();
  let lastScrolled = null;
  const mobile = globalThis.window?.matchMedia?.(MOBILE_QUERY) || null;
  const isVertical = () => !(mobile?.matches);

  function build() {
    const ordered = elevatorOrder(store.get().office.floors, { vertical: isVertical() });
    clear(list);
    items.clear();
    lastScrolled = null;
    ordered.forEach(({ floor: f, idx }) => {
      // The real floor number (order + 1): a member who sees only some floors reads the admin's numbers.
      const n = floorNumber(f, idx);
      const num = h('span', { class: 'ef-num' }, pad2(n));
      const name = h('span', { class: 'ef-name' }, f.name);
      const meta = h('span', { class: 'ef-meta mono-label' });
      const dot = h('span', { class: 'ef-dot', 'aria-hidden': 'true' });
      // « À régler » on that floor (web/ui/notifications.js): a small red count.
      const alert = h('span', { class: 'ef-alert', 'aria-hidden': 'true', hidden: true });
      const swatches = h('span', { class: 'ef-swatches', 'aria-hidden': 'true' },
        floorDepartments(store.get().office.departments, f.id).slice(0, 8)
          .map((d) => h('span', { class: 'ef-swatch', style: { '--c': deptColor(d.color, d.id) } })));
      const btn = h('button', {
        type: 'button', class: 'elevator-floor', dataset: { floor: f.id },
        title: n <= 9 ? `${f.name} (${n})` : f.name,
        onClick: () => onSelectFloor?.(f.id),
      }, num, h('span', { class: 'ef-text' }, name, meta, swatches), dot, alert);
      list.appendChild(h('li', {}, btn));
      items.set(f.id, { btn, meta, dot, alert, name: f.name, idx, n });
    });
    refresh();
  }

  function refresh() {
    const s = store.get();
    const floorsStats = s.stats?.floors || {};
    for (const [id, it] of items) {
      const st = floorsStats[id] || { doing: 0, waiting: 0 };
      const parts = [];
      if (st.doing) parts.push(t('elevator.doing', { n: st.doing }));
      if (st.waiting) parts.push(t('elevator.waiting', { n: st.waiting }));
      // One count per line (a narrow elevator would otherwise end a line on a dangling " ·").
      setText(it.meta, parts.length ? parts.join('\n') : t('elevator.calm'));
      it.dot.className = classNames(['ef-dot', `is-${floorActivity(st)}`]);
      const todo = Math.max(0, Number(s.notices?.counts?.[id]) || 0);
      // Among them, the person's finished requests: blue when there is nothing else.
      const done = Math.min(todo, Math.max(0, Number(s.notices?.doneCounts?.[id]) || 0));
      setText(it.alert, badgeText(todo));
      it.alert.hidden = !todo;
      it.alert.classList.toggle('is-info', todo > 0 && done >= todo);
      it.btn.classList.toggle('has-notices', todo > 0);
      const active = s.ui.view !== 'building' && s.ui.floorId === id;
      setAttr(it.btn, 'aria-current', active ? 'page' : null);
      it.btn.classList.toggle('is-active', active);
      const problems = todo - done;
      const alertText = `${problems ? ` · ${t('elevator.notices', { n: problems })}` : ''}${done ? ` · ${t('elevator.doneNotices', { n: done })}` : ''}`;
      setAttr(it.btn, 'aria-label', `${t('elevator.floorAria', { n: it.n, name: it.name })} — ${parts.length ? parts.join(' · ') : t('elevator.calm')}${alertText}`);
      // Phone chips: bring the current floor into view only when it changes (every ui change —
      // hover, filters, sheet tabs — would otherwise undo the user's own scrolling).
      if (active && lastScrolled !== id && typeof it.btn.scrollIntoView === 'function' && mobile?.matches) {
        lastScrolled = id;
        it.btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
    const building = s.ui.view === 'building';
    if (building) lastScrolled = null;
    buildingBtn.classList.toggle('is-active', building);
    setAttr(buildingBtn, 'aria-pressed', String(building));
  }

  build();
  let lastFloors = store.get().office.floors;
  let lastDepts = store.get().office.departments;
  const onMedia = () => build();
  mobile?.addEventListener?.('change', onMedia);
  const offs = [
    store.on('office', (office) => {
      if (office.floors !== lastFloors || office.departments !== lastDepts) {
        lastFloors = office.floors;
        lastDepts = office.departments;
        build();
      }
    }),
    store.on('ui', refresh),
    store.on('stats', refresh),
    store.on('notices', refresh),
    () => mobile?.removeEventListener?.('change', onMedia),
  ];
  return { el, destroy() { for (const off of offs) off(); } };
}

/**
 * Zoom in / out / recentre buttons for the 3D scene (ctx.scene is read at click time).
 * @returns {{el: HTMLElement}}
 */
export function createSceneControls(ctx) {
  const call = (fn, ...args) => { try { ctx.scene?.[fn]?.(...args); } catch (err) { console.error(err); } };
  const el = h('div', { class: 'scene-controls', role: 'group', 'aria-label': t('scene.controls') },
    iconButton('plus', t('scene.zoomIn'), { onClick: () => call('zoom', 1) }),
    iconButton('minus', t('scene.zoomOut'), { onClick: () => call('zoom', -1) }),
    iconButton('home', t('scene.reset'), { onClick: () => call('resetView') }));
  return { el };
}
